import { useEffect, useRef, useState, useContext } from "react";
import { useParams } from "react-router";
import { Card } from "primereact/card";
import AppBreadcrumb from "../../components/CustomeBreadcrumb/AppBreadcrumb";
import {
  defaultPageRowOptions,
  defaultRowOptions,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
} from "../../appconfig/Settings";
import { getSpecificDetails, postAllData } from "../../service/ProductService";
import { GET_EMPLOYEE_BY_ID, UPDATE_PAYSTUB_DATA, VIEW_SIN_NUMBER } from "../../config/ApiConstant";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { EmployeePaystubListColumns } from "../../appconfig/DatatableSetting";
import { Column } from "primereact/column";
import { formatDate } from "../../appconfig/AppHelper";
import { Dialog } from "primereact/dialog";
import { validatePayrollForm } from "../../config/Validate";
import { createdAtTemplate, dateTemplate, payPeriodTemplate } from "../../appconfig/TemplateFieldSetting";
import Loader from "../../components/Loader/Loader";
import ViewSinNumber from "../../components/ViewSinNumber/ViewSinNumber";

const EmployeeDetail = () => {
  const { id }: any = useParams();
  const employee_id = id
    ? (() => {
      try {
        return atob(id);
      } catch {
        return null;
      }
    })()
    : null;
  const toast = useRef<any>(null);
  // get userData from local storage
  const rawUserData = localStorage.getItem("user_data");
  let userData: any = null;
  userData = rawUserData ? JSON.parse(rawUserData) : null;
  const [employeeData, setEmployeeData] = useState<any>(null);
  const [globalFieldFilter, setGlobalFieldFilter] = useState<any>(null);
  const [globalFilter, setGlobalFilter] = useState<any>(null);
  const [pageLoad, setPageLoad] = useState<boolean>(false);
  const [paystubList, setPaystubList] = useState<any>([]);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [addDeleteEmployeeDialog, setAddDeleteEmployeeDialog] = useState<boolean>(false);
  const [errors, setErrors] = useState<any>({});
  const [hours, setHours] = useState<any>(null);

  useEffect(() => {
    fetchEmployeeDetail();
  }, [id]);

  // fetch employee detail api
  const fetchEmployeeDetail = async () => {
    try {
      const response = await getSpecificDetails(GET_EMPLOYEE_BY_ID, employee_id);
      const responseData = response?.employee_details;
      const data = response?.working_hour_list;
      setEmployeeData(responseData);
      setPaystubList(data);
      setPageLoad(true);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message,
      });
      setPageLoad(false);
    }
  };

  // clear all filter
  const clearAll = () => {
    setGlobalFilter(null);
    setGlobalFieldFilter(null);
  };

  // view pdf
  const viewPdf = (rowData: any) => {
    if (rowData?.paystub_url) {
      window.open(rowData?.paystub_url, "_blank");
    } else {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "PDF NOT FOUND",
      });
    }
  };

  // action body template
  const actionBodyTemplate = (rowData: any) => (
    <div className="actions">
      <Button
        icon="pi pi-file-pdf"
        tooltip="View PDF"
        className="p-button-secondary common-view-pdf-btn"
        tooltipOptions={{ position: "bottom" }}
        onClick={() => viewPdf(rowData)}
      />
    </div>
  );

  // hide add & edit employee modal
  const hideAddEditEmployeeDialog = () => {
    setAddDeleteEmployeeDialog(false);
    setSubmitLoading(false);
    setHours(null);
  };

  // add & update employee api call
  const submitEmployeeForm = async () => {
    const { isError, errors } = validatePayrollForm(hours);
    setErrors(errors);
    if (isError) return;

    setSubmitLoading(true);
    try {
      const payload = {
        hour: hours,
        user_id: employee_id,
      };
      const response = await postAllData(UPDATE_PAYSTUB_DATA, payload);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        hideAddEditEmployeeDialog();
        fetchEmployeeDetail();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  // OnChange
  const onInputChange = (e: any) => {
    setHours(e.target.value);
  };

  // left toolbar template
  const leftToolbarTemplate = () => (
    <span className="block mr-2 p-input-icon-left">
      <i className="pi pi-search" />
      <InputText type="search" value={globalFilter || ""} onInput={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
    </span>
  );

  // right toolbar template
  const rightToolbarTemplate = () => (
    <>
      <Button icon="pi pi-filter-slash" label="Clear" onClick={clearAll} className="mr-2 p-button-outlined common-p-outline-button" />
    </>
  );

  return (
    <>
      <AppBreadcrumb currentMenu="Employee" pageName=" Detail" pageTitle="Employee Detail" goToPage={true} />

      <Card className="bg-white lg:p-2 crud-demo relative" style={{ marginBottom: "2rem" }}>
        {employeeData ? (
          <div className="grid">
            <div className="col-12 md:col-12">
              <div className="grid p-fluid">
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Name:</label>
                  <div>
                    {employeeData?.first_name || "-"} {employeeData?.last_name || "-"}
                  </div>
                </div>
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Email:</label>
                  <div>{employeeData?.email || "-"}</div>
                </div>

                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Occupation:</label>
                  <div>{employeeData?.occupation || "-"}</div>
                </div>

                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Sin Number:</label>
                  <ViewSinNumber label="SIN Number" value={employeeData?.sin_number} />
                </div>

                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Date of Birth:</label>
                  <div>{formatDate(employeeData?.date_of_birth) || "-"}</div>
                </div>
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Joining Date:</label>
                  <div>{employeeData?.joining_date ? formatDate(employeeData?.joining_date) : "-"}</div>
                </div>
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Is Vacation Payout On Hold:</label>
                  <div>{employeeData?.vacation_hold === true ? "Yes" : "No"}</div>
                </div>
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Vacation Payout:</label>
                  <div>{employeeData.vaccation_payout ? `${employeeData.vaccation_payout}%` : "-"}</div>
                </div>
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Is EI Exempt:</label>
                  <div>{employeeData?.ei_exempt === true ? "Yes" : "No"}</div>
                </div>
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Is CPP Exempt:</label>
                  <div>{employeeData?.cpp_exempt === true ? "Yes" : "No"}</div>
                </div>
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>{employeeData?.is_salary === true ? "Salary" : "Wage"}:</label>
                  <div>{employeeData.wage ? `$${employeeData.wage}` : "-"}</div>
                </div>
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Tax Deduction:</label>
                  <div>{employeeData.tax_deduction ? `${employeeData.tax_deduction}%` : "-"}</div>
                </div>
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Status:</label>
                  <div>{employeeData.emp_status ? `${employeeData.emp_status}` : "-"}</div>
                </div>
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Status Date:</label>
                  <div>{employeeData.emp_status_date ? `${formatDate(employeeData.emp_status_date)}` : "-"}</div>
                </div>
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Phone:</label>
                  <div>{employeeData?.phone || "-"}</div>
                </div>
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Province:</label>
                  <div>{employeeData?.province || "-"}</div>
                </div>

                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Street Address:</label>
                  <div>{employeeData?.address || "-"}</div>
                </div>
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>City:</label>
                  <div>{employeeData?.city || "-"}</div>
                </div>
                <div className="mb-1 md:mb-3 col-12 sm:col-6 md:col-4 lg:col-3">
                  <label style={{ fontWeight: "bold" }}>Postal Code:</label>
                  <div>{employeeData?.postal_code || "-"}</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          ""
        )}

        <hr />

        <h5 className="mb-3">Employee Paystub Details</h5>
        <Toast ref={toast} />
        <Toolbar className="gap-3" left={leftToolbarTemplate} right={rightToolbarTemplate} />
        {pageLoad ? (
          <DataTable
            value={paystubList}
            showGridlines
            dataKey="id"
            stripedRows
            responsiveLayout={"scroll"}
            globalFilter={globalFilter}
            rows={defaultRowOptions}
            paginator={paystubList.length > 0}
            rowsPerPageOptions={defaultPageRowOptions}
            className="datatable-responsive p-datatble-sm"
            paginatorTemplate={paginatorLinks}
            currentPageReportTemplate={showingEntries + " Users"}
            emptyMessage="No Paystub Found"
            filters={globalFieldFilter}
            onFilter={(e) => setGlobalFieldFilter(e.filters)}
            removableSort
          >
            {EmployeePaystubListColumns.map((col, i) => {
              if (col.field === "createdAt") {
                return <Column key={col.field} field={col.field} header={col.header} body={dateTemplate} sortable />;
              } else if (col.field === "pay_period") {
                return <Column key={col.field} field={col.field} header={col.header} body={payPeriodTemplate} />;
              } else if (col.field === "paystub_url") {
                return <Column key={col.field} field={col.field} header={col.header} body={actionBodyTemplate} />;
              } else {
                return <Column key={col.field} field={col.field} header={col.header} />;
              }
            })}
          </DataTable>
        ) : (
          <DataTable value={Skeletonitems}>
            {EmployeePaystubListColumns.map((col) => (
              <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
            ))}
          </DataTable>
        )}
        {pageLoad === false && <Loader />}
      </Card>

      <Dialog
        visible={addDeleteEmployeeDialog}
        style={{ width: "800px" }}
        header="Add Working Hours"
        modal
        footer={
          <div className="flex pt-2 align-items-center justify-content-end">
            <Button label="Cancel" icon="pi pi-times" className="mr-4 p-button-secondary" onClick={hideAddEditEmployeeDialog} />
            <Button label="Submit" icon="pi pi-check" className="common-primary-button" onClick={submitEmployeeForm} loading={submitLoading} />
          </div>
        }
        onHide={hideAddEditEmployeeDialog}
      >
        <div className="grid p-fluid formgrid">
          {/* Working hour  */}
          <div className="field col-12 md:col-6">
            <label htmlFor="number">Working Hour *</label>
            <div className="input-effect-div">
              <InputText
                value={hours || ""}
                name="hour"
                onChange={(e) => onInputChange(e)}
                className={errors["hour"] && "p-invalid"}
                keyfilter="pint"
              />

              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["hour"]}</small>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default EmployeeDetail;
